/**
 * Sidebar Component (TypeScript)
 *
 * Navegação lateral do ERP com suporte a permissões.
 * Adaptação visual conforme especificação de cores em `docs/ajuste-sidebar/extrutura-cores-sidebar.md`.
 */

import React, { useEffect, useMemo, useRef, useState, MouseEvent } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, ChevronRight, ChevronLeft, ChevronDown } from 'lucide-react';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../contexts/AuthContext';
import { ERP_MENU_ITEMS, filterErpMenuItems, isErpParentItemActive, isErpSubmenuItemActive } from '../../../config/erpNavigation';
import './Sidebar.css';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
}

const menuItems = ERP_MENU_ITEMS;

function Sidebar({ collapsed, onToggle }: SidebarProps) {
  const location = useLocation();
  const { hasPermission, hasModuleAccess, loading: permissionsLoading } = usePermissions();
  const { isAdmin } = useAuth();

  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const [clickedItem, setClickedItem] = useState<string | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState<{ top: number }>({ top: 0 });
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const dropdownRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const menuItemRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const hoverLeaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const routeAccessCtx = useMemo(
    () => ({
      isAdmin: isAdmin(),
      permissionsLoading,
      hasPermission,
      hasModuleAccess,
    }),
    [isAdmin, permissionsLoading, hasPermission, hasModuleAccess]
  );

  const filteredMenuItems = useMemo(
    () => filterErpMenuItems(menuItems, routeAccessCtx),
    [routeAccessCtx]
  );

  const toggleSubmenu = (itemId: string) => {
    if (collapsed) {
      const newClickedItem = clickedItem === itemId ? null : itemId;
      setClickedItem(newClickedItem);
      setHoveredItem(null);
      if (newClickedItem) updateDropdownPosition(itemId);
    } else {
      setExpandedItems((prev) => (prev.includes(itemId) ? prev.filter((id) => id !== itemId) : [...prev, itemId]));
    }
  };

  const updateDropdownPosition = (itemId: string) => {
    const menuItemRef = menuItemRefs.current[itemId];
    if (menuItemRef) {
      const rect = menuItemRef.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const padding = 10;
      let top = rect.top;

      const menuItem = filteredMenuItems.find((item) => item.id === itemId);
      const submenuItemsCount = menuItem?.submenu?.length || 0;
      const estimatedDropdownHeight = 60 + submenuItemsCount * 50 + 20;
      const maxDropdownHeight = viewportHeight - padding * 2;
      const actualDropdownHeight = Math.min(estimatedDropdownHeight, maxDropdownHeight);

      if (top + actualDropdownHeight > viewportHeight - padding) {
        const overflow = top + actualDropdownHeight - (viewportHeight - padding);
        top = overflow < actualDropdownHeight * 0.5 ? top - overflow : viewportHeight - actualDropdownHeight - padding;
        if (top < padding) top = padding;
      }
      setDropdownPosition({ top });
    }
  };

  const handleMouseEnter = (itemId: string) => {
    if (collapsed) {
      if (hoverLeaveTimer.current) {
        clearTimeout(hoverLeaveTimer.current);
        hoverLeaveTimer.current = null;
      }
      setHoveredItem(itemId);
      setClickedItem(null);
      updateDropdownPosition(itemId);
    }
  };

  const handleMouseLeave = () => {
    if (collapsed) {
      hoverLeaveTimer.current = setTimeout(() => {
        setHoveredItem(null);
      }, 150);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (collapsed && clickedItem) {
        const clickedItemRef = dropdownRefs.current[clickedItem];
        if (
          sidebarRef.current &&
          !sidebarRef.current.contains(event.target as Node) &&
          clickedItemRef &&
          !clickedItemRef.contains(event.target as Node)
        ) {
          setClickedItem(null);
        }
      }
    };

    if (clickedItem) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [clickedItem, collapsed]);

  const handleCtrlClick = (e: MouseEvent, path?: string) => {
    if (!path) return;
    if (e.ctrlKey || e.metaKey) {
      e.preventDefault();
      window.open(path, '_blank');
    }
  };

  const isActive = (path?: string) =>
    path ? location.pathname === path || location.pathname.startsWith(`${path}/`) : false;

  const isParentActive = (item: (typeof filteredMenuItems)[number]) =>
    isErpParentItemActive(location.pathname, item);

  const isSubItemActive = (item: (typeof filteredMenuItems)[number], path: string) =>
    item.submenu ? isErpSubmenuItemActive(location.pathname, item.submenu, path) : isActive(path);

  const isExpanded = !collapsed;
  const showDropdown = (itemId: string) => collapsed && (hoveredItem === itemId || clickedItem === itemId);

  return (
    <aside ref={sidebarRef} className={`erp-sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <button className="sidebar-toggle" onClick={onToggle} aria-label={collapsed ? 'Expandir menu' : 'Recolher menu'}>
          {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
        </button>
      </div>

      <nav className="sidebar-nav">
        {filteredMenuItems.map((item) => {
          const hasChildren = !!item.submenu;
          return (
            <div key={item.id} className="sidebar-item">
              {hasChildren ? (
                <>
                  <button
                    ref={(el) => (menuItemRefs.current[item.id] = el)}
                    className={`sidebar-link ${isParentActive(item) ? 'active' : ''} ${
                      showDropdown(item.id) ? 'dropdown-active' : ''
                    }`}
                    onClick={(e) => { handleCtrlClick(e, item.path); if (!e.ctrlKey && !e.metaKey) toggleSubmenu(item.id); }}
                    onMouseEnter={() => handleMouseEnter(item.id)}
                    onMouseLeave={handleMouseLeave}
                  >
                    <span className="sidebar-icon">
                      <item.icon size={20} />
                    </span>
                    {isExpanded && <span className="sidebar-label">{item.label}</span>}
                    {isExpanded && (
                      <span className="sidebar-arrow">
                        {expandedItems.includes(item.id) ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                      </span>
                    )}
                  </button>

                  {isExpanded && expandedItems.includes(item.id) && (
                    <div className="sidebar-submenu">
                      {item.submenu?.map((subItem) => {
                        const SubIcon = subItem.icon;
                        const openInNewTab =
                          subItem.path === '/pedidos' ||
                          subItem.path === '/erp/vendas/vendas' ||
                          subItem.path === '/erp/vendas/pre-venda' ||
                          subItem.path === '/erp/vendas/tv-display';

                        if (openInNewTab) {
                          return (
                            <button
                              key={subItem.path}
                              onClick={() => window.open(subItem.path, '_blank')}
                              className={`sidebar-sublink ${isSubItemActive(item, subItem.path) ? 'active' : ''}`}
                            >
                              <span className="sidebar-icon">{SubIcon && <SubIcon size={18} />}</span>
                              <span className="sidebar-label">{subItem.label}</span>
                            </button>
                          );
                        }

                        return (
                          <Link
                            key={subItem.path}
                            to={subItem.path}
                            className={`sidebar-sublink ${isSubItemActive(item, subItem.path) ? 'active' : ''}`}
                            onClick={(e) => handleCtrlClick(e, subItem.path)}
                          >
                            <span className="sidebar-icon">{SubIcon && <SubIcon size={18} />}</span>
                            <span className="sidebar-label">{subItem.label}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}

                  {showDropdown(item.id) && (
                    <div
                      ref={(el) => (dropdownRefs.current[item.id] = el)}
                      className="sidebar-dropdown"
                      style={{ top: `${dropdownPosition.top}px` }}
                      onMouseEnter={() => {
                        if (hoverLeaveTimer.current) {
                          clearTimeout(hoverLeaveTimer.current);
                          hoverLeaveTimer.current = null;
                        }
                        setHoveredItem(item.id);
                      }}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="sidebar-dropdown-header">
                        <item.icon size={18} />
                        <span>{item.label}</span>
                      </div>
                      <div className="sidebar-dropdown-menu">
                        {item.submenu?.map((subItem) => {
                          const SubIcon = subItem.icon;
                          const openInNewTab =
                            subItem.path === '/pedidos' ||
                            subItem.path === '/erp/vendas/vendas' ||
                            subItem.path === '/erp/vendas/pre-venda' ||
                            subItem.path === '/erp/vendas/tv-display';

                          if (openInNewTab) {
                            return (
                              <button
                                key={subItem.path}
                                onClick={() => {
                                  window.open(subItem.path, '_blank');
                                  setClickedItem(null);
                                  setHoveredItem(null);
                                }}
                                className={`sidebar-dropdown-item ${isSubItemActive(item, subItem.path) ? 'active' : ''}`}
                              >
                                {SubIcon && <SubIcon size={16} />}
                                <span>{subItem.label}</span>
                              </button>
                            );
                          }

                          return (
                            <Link
                              key={subItem.path}
                              to={subItem.path}
                              className={`sidebar-dropdown-item ${isSubItemActive(item, subItem.path) ? 'active' : ''}`}
                              onClick={(e) => {
                                handleCtrlClick(e, subItem.path);
                                setClickedItem(null);
                                setHoveredItem(null);
                              }}
                            >
                              {SubIcon && <SubIcon size={16} />}
                              <span>{subItem.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={item.path!}
                  className={`sidebar-link ${isActive(item.path) ? 'active' : ''}`}
                  onClick={(e) => handleCtrlClick(e, item.path)}
                >
                  <span className="sidebar-icon">
                    <item.icon size={20} />
                  </span>
                  {isExpanded && <span className="sidebar-label">{item.label}</span>}
                </Link>
              )}
            </div>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <Link to="/" className="sidebar-link">
          <span className="sidebar-icon">
            <Home size={20} />
          </span>
          {isExpanded && <span className="sidebar-label">Voltar ao E-commerce</span>}
        </Link>
      </div>
    </aside>
  );
}

export default Sidebar;

